import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export type BadgeTone = "neutral" | "info" | "good" | "warning" | "serious" | "critical";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Optional leading dot — a secondary (non-color-alone) status channel. */
  dot?: boolean;
}

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: "bg-muted/12 text-ink-secondary ring-hairline",
  info: "bg-brand/12 text-brand ring-brand/25",
  good: "bg-good/12 text-good-text ring-good/30",
  warning: "bg-warning/15 text-serious ring-warning/35",
  serious: "bg-serious/15 text-serious ring-serious/35",
  critical: "bg-critical/12 text-critical ring-critical/30",
};

const DOT_CLASS: Record<BadgeTone, string> = {
  neutral: "bg-muted",
  info: "bg-brand",
  good: "bg-good",
  warning: "bg-warning",
  serious: "bg-serious",
  critical: "bg-critical",
};

/**
 * Small status pill. `dot` adds a leading indicator so state is legible without
 * relying on hue alone (the palette's accessibility rule for status colors).
 */
export function Badge({
  tone = "neutral",
  dot = false,
  className,
  children,
  ...rest
}: BadgeProps): ReactNode {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        TONE_CLASS[tone],
        className,
      )}
      {...rest}
    >
      {dot && <span className={cn("size-1.5 rounded-full", DOT_CLASS[tone])} aria-hidden="true" />}
      {children}
    </span>
  );
}
